// Form validation utilities
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: string) => string | null;
}

export interface FieldValidation {
  [fieldName: string]: ValidationRule;
}

export const validateField = (value: string, rules: ValidationRule): string[] => {
  const errors: string[] = [];

  if (rules.required && (!value || value.trim() === '')) {
    errors.push('This field is required');
  }

  if (value && rules.minLength && value.length < rules.minLength) {
    errors.push(`Minimum length is ${rules.minLength} characters`);
  }

  if (value && rules.maxLength && value.length > rules.maxLength) {
    errors.push(`Maximum length is ${rules.maxLength} characters`);
  }

  if (value && rules.pattern && !rules.pattern.test(value)) {
    errors.push('Invalid format');
  }

  if (value && rules.custom) {
    const customError = rules.custom(value);
    if (customError) {
      errors.push(customError);
    }
  }

  return errors;
};

export const validateForm = (data: Record<string, string>, validationRules: FieldValidation): ValidationResult => {
  const errors: string[] = [];
  let isValid = true;

  Object.keys(validationRules).forEach(fieldName => {
    const fieldErrors = validateField(data[fieldName] || '', validationRules[fieldName]);
    if (fieldErrors.length > 0) {
      isValid = false;
      errors.push(...fieldErrors.map(error => `${fieldName}: ${error}`));
    }
  });

  return { isValid, errors };
};

// Common validation patterns
export const validationPatterns = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^\+?[\d\s\-()]+$/,
  url: /^https?:\/\/.+/,
  alphanumeric: /^[a-zA-Z0-9]+$/,
};

// Common validation rules
export const commonRules = {
  required: { required: true },
  email: { 
    required: true, 
    pattern: validationPatterns.email,
    custom: (value: string) => {
      if (!validationPatterns.email.test(value)) {
        return 'Please enter a valid email address';
      }
      return null;
    }
  },
  password: {
    required: true,
    minLength: 8,
    custom: (value: string) => {
      if (value.length < 8) {
        return 'Password must be at least 8 characters long';
      }
      if (!/(?=.*[a-z])/.test(value)) {
        return 'Password must contain at least one lowercase letter';
      }
      if (!/(?=.*[A-Z])/.test(value)) {
        return 'Password must contain at least one uppercase letter';
      }
      if (!/(?=.*\d)/.test(value)) {
        return 'Password must contain at least one number';
      }
      return null;
    }
  },
  phone: {
    required: true,
    pattern: validationPatterns.phone,
    custom: (value: string) => {
      if (!validationPatterns.phone.test(value)) {
        return 'Please enter a valid phone number';
      }
      return null;
    }
  },
  businessName: {
    required: true,
    minLength: 2,
    maxLength: 100,
    custom: (value: string) => {
      if (value.length < 2) {
        return 'Business name must be at least 2 characters long';
      }
      if (value.length > 100) {
        return 'Business name must be less than 100 characters';
      }
      return null;
    }
  }
};
